import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { SubscriptionProvider } from './hooks/useSubscriptionStatus';
import { lazyWithRetry as lazy } from './utils/lazyWithRetry';
// Dominio propio de la tienda: bajo un host de cliente monta solo el
// storefront. En hosts propios no hace nada. Ver components/StorefrontGate.
import StorefrontGate from './components/StorefrontGate';

// Eager: Login es el destino mas comun en el primer arranque, queremos que
// aparezca sin esperar un chunk extra.
import Login from './pages/Login';
import { hasPublishedDemoVideos } from './data/publicSite';

// Lazy: el resto de las rutas se baja bajo demanda. Cada import() se vuelve
// un chunk separado en build (Rollup nombra automaticamente), y la app
// inicial deja de arrastrar el ERP entero solo para mostrar /login.
const Registro = lazy(() => import('./pages/RegistroOnboarding'));
const Landing = lazy(() => import('./pages/Landing'));
const Demo = lazy(() => import('./pages/Demo'));
const PedirDemo = lazy(() => import('./pages/PedirDemo'));
const Funcionalidades = lazy(() => import('./pages/Funcionalidades'));
const Precios = lazy(() => import('./pages/Precios'));
const Nosotros = lazy(() => import('./pages/Nosotros'));
const Soluciones = lazy(() => import('./pages/Soluciones'));
const TerminosServicio = lazy(() => import('./pages/TerminosServicio'));
const PoliticaPrivacidad = lazy(() => import('./pages/PoliticaPrivacidad'));
const AcademiaImpulza = lazy(() => import('./pages/AcademiaImpulza'));
const AcademiaViewer = lazy(() => import('./pages/academia/AcademiaViewer'));
const Sistema = lazy(() => import('./pages/Sistema'));
const Onboarding = lazy(() => import('./pages/Onboarding'));
const Tienda = lazy(() => import('./pages/Tienda'));
const Rastreo = lazy(() => import('./pages/Rastreo'));

import './index.css';

const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#F2F9FF]">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#3B82F6]" />
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <RouteFallback />;
  }

  if (!user) return <Navigate to="/login" replace />;
  return children;
};

export default function App() {
  return (
    <div className="min-h-screen bg-[#F2F9FF]">
      <Suspense fallback={<RouteFallback />}>
        <StorefrontGate>
        <Routes>
          {/* Auth */}
          <Route path="/login" element={<Login />} />
          <Route path="/registro" element={<Registro />} />

          {/* Pedir Demo */}
          <Route path="/demo" element={hasPublishedDemoVideos() ? <Demo /> : <Navigate to="/pedir-demo" replace />} />
          <Route path="/pedir-demo" element={<PedirDemo />} />

          {/* Funcionalidades */}
          <Route path="/funcionalidades" element={<Funcionalidades />} />

          {/* Precios */}
          <Route path="/precios" element={<Precios />} />

          {/* Nosotros */}
          <Route path="/nosotros" element={<Nosotros />} />

          {/* Soluciones */}
          <Route path="/soluciones" element={<Soluciones />} />
          <Route path="/soluciones/kioscos" element={<Soluciones />} />
          <Route path="/soluciones/indumentaria" element={<Soluciones />} />
          <Route path="/soluciones/gastronomia" element={<Soluciones />} />

          {/* Legales */}
          <Route path="/legales/terminos" element={<TerminosServicio />} />
          <Route path="/legales/privacidad" element={<PoliticaPrivacidad />} />

          {/* Tienda publica */}
          <Route path="/tienda/:slug" element={<Tienda />} />
          <Route path="/rastreo/:trackingCode" element={<Rastreo />} />
          <Route
            path="/sistema/onboarding"
            element={
              <ProtectedRoute>
                <Onboarding />
              </ProtectedRoute>
            }
          />

          {/* ERP */}
          <Route
            path="/sistema/*"
            element={
              <ProtectedRoute>
                <SubscriptionProvider>
                  <Sistema />
                </SubscriptionProvider>
              </ProtectedRoute>
            }
          />

          {/* Compatibilidad legacy: algunas instalaciones viejas siguen entrando
              por esta URL. La llevamos a la pantalla vigente dentro del ERP. */}
          <Route path="/super-admin-pricing" element={<Navigate to="/sistema/listas-precios" replace />} />

          {/* Default */}
          {/* La raiz carga la Landing comercial */}
          <Route path="/" element={<Landing />} />

          <Route path="*" element={<Navigate to="/sistema" replace />} />
        </Routes>
        </StorefrontGate>
      </Suspense>
    </div>
  );
}
